/**
 * dmI18n — lightweight i18n helper used across /portal/* pages.
 *
 * Loaded by MasterPage-DMX.master. Kicks off the shared scope automatically
 * and exposes window.dmI18nReady (a Promise) so pages can `await` before
 * calling t() or applyTo().
 *
 * API
 * ---
 *   await dmI18n.init({ scopes: ['/i18n/{locale}.json'] })
 *       Sets the active locale + loads + merges one or more JSON scopes.
 *       URLs may contain `{locale}` which gets replaced with the current
 *       locale before fetch. Safe to call multiple times — each call appends
 *       to the existing translation table (last write wins per key).
 *
 *   dmI18n.t('common.save')                    → "Uložit" / "Save"
 *   dmI18n.t('items.foundForCompany', {n:5})  → variable interpolation {var}
 *   dmI18n.plural(5, { one: 'rok', few: 'roky', many: 'let' })
 *
 *   dmI18n.applyTo(rootEl?)
 *       Walks rootEl (defaults to document) and applies translations
 *       declaratively via these attributes:
 *           data-i18n             → element.textContent
 *           data-i18n-html        → element.innerHTML (use carefully — HTML is
 *                                   taken from the JSON; not for user-typed
 *                                   strings)
 *           data-i18n-placeholder → element.placeholder
 *           data-i18n-title       → element.title
 *           data-i18n-aria-label  → element.setAttribute('aria-label', …)
 *           data-i18n-value       → element.value (use for <input type=button>)
 *       Pages call applyTo() once after init+load, plus again after any
 *       dynamic content render.
 *
 *   dmI18n.getLocale()                         → 'cs' | 'en' | …
 *   dmI18n.setLocale('en')                     → saves to localStorage, reloads
 *   dmI18n.supportedLocales()                  → ['cs', 'en', …]
 *
 * Locale resolution priority on init()
 * ------------------------------------
 *   1. `?lang=xx` URL override (handy for debugging without flipping storage)
 *   2. localStorage['dm.locale']
 *   3. navigator.language (first 2 chars)
 *   4. DEFAULT_LOCALE constant ('cs')
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'dm.locale';
  var DEFAULT_LOCALE = 'cs';
  var SUPPORTED_LOCALES = ['cs', 'en'];

  var translations = {};   // flat key -> string map
  var locale = null;
  var pluralRules = null;

  function detectLocale() {
    // URL override
    var m = /[?&]lang=([a-zA-Z-]+)/.exec(window.location.search);
    if (m) {
      var l = m[1].slice(0, 2).toLowerCase();
      if (SUPPORTED_LOCALES.indexOf(l) !== -1) return l;
    }
    // localStorage
    try {
      var saved = localStorage.getItem(STORAGE_KEY);
      if (saved && SUPPORTED_LOCALES.indexOf(saved) !== -1) return saved;
    } catch (e) { /* localStorage might be disabled */ }
    // navigator
    var nav = (navigator.language || DEFAULT_LOCALE).slice(0, 2).toLowerCase();
    if (SUPPORTED_LOCALES.indexOf(nav) !== -1) return nav;
    return DEFAULT_LOCALE;
  }

  function flatten(obj, prefix, target) {
    Object.keys(obj || {}).forEach(function (k) {
      var v = obj[k];
      var key = prefix ? prefix + '.' + k : k;
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        flatten(v, key, target);
      } else {
        target[key] = v;
      }
    });
  }

  function interpolate(str, vars) {
    if (vars == null) return str;
    return String(str).replace(/\{(\w+)\}/g, function (_, k) {
      return vars[k] !== undefined ? vars[k] : '{' + k + '}';
    });
  }

  async function fetchScope(url) {
    try {
      var r = await fetch(url, { cache: 'no-cache' });
      if (!r.ok) return {};
      return await r.json();
    } catch (e) {
      return {};
    }
  }

  function pickAttr(el, attr, defaultLocale) {
    // For data-i18n="…" the element's existing content becomes the implicit
    // Czech (DEFAULT_LOCALE) default in case the key isn't in the JSON. We
    // don't write back any cache — `t()` falls back to the key when missing.
    return el.getAttribute(attr);
  }

  window.dmI18n = {
    init: async function (opts) {
      opts = opts || {};
      if (locale == null) {
        locale = opts.locale || detectLocale();
        try { pluralRules = new Intl.PluralRules(locale); } catch (e) { pluralRules = null; }
      }
      if (opts.scopes && opts.scopes.length) {
        var urls = opts.scopes.map(function (s) { return s.replace('{locale}', locale); });
        var jsons = await Promise.all(urls.map(fetchScope));
        jsons.forEach(function (j) { flatten(j, '', translations); });
      }
      return locale;
    },
    t: function (key, vars) {
      var s = translations[key];
      if (s == null) return key;  // returning the key makes missing strings obvious in the UI
      return interpolate(s, vars);
    },
    plural: function (count, forms) {
      var cat = pluralRules ? pluralRules.select(count) : (count === 1 ? 'one' : 'other');
      return forms[cat] || forms.other || forms.one || '';
    },
    applyTo: function (root) {
      root = root || document;
      // textContent
      root.querySelectorAll('[data-i18n]').forEach(function (el) {
        el.textContent = window.dmI18n.t(pickAttr(el, 'data-i18n'));
      });
      // innerHTML (HTML taken from the JSON, NOT from user input)
      root.querySelectorAll('[data-i18n-html]').forEach(function (el) {
        el.innerHTML = window.dmI18n.t(pickAttr(el, 'data-i18n-html'));
      });
      // attributes
      root.querySelectorAll('[data-i18n-placeholder]').forEach(function (el) {
        el.setAttribute('placeholder', window.dmI18n.t(pickAttr(el, 'data-i18n-placeholder')));
      });
      root.querySelectorAll('[data-i18n-title]').forEach(function (el) {
        el.setAttribute('title', window.dmI18n.t(pickAttr(el, 'data-i18n-title')));
      });
      root.querySelectorAll('[data-i18n-aria-label]').forEach(function (el) {
        el.setAttribute('aria-label', window.dmI18n.t(pickAttr(el, 'data-i18n-aria-label')));
      });
      root.querySelectorAll('[data-i18n-value]').forEach(function (el) {
        el.value = window.dmI18n.t(pickAttr(el, 'data-i18n-value'));
      });
    },
    getLocale: function () { return locale; },
    setLocale: function (newLocale) {
      if (SUPPORTED_LOCALES.indexOf(newLocale) === -1) return;
      try { localStorage.setItem(STORAGE_KEY, newLocale); } catch (e) { /* swallow */ }
      window.location.reload();
    },
    supportedLocales: function () { return SUPPORTED_LOCALES.slice(); }
  };

  // Kick off the shared scope right away. MasterPage-DMX exposes the resolved
  // Promise as window.dmI18nReady so page-level scripts can `await` before
  // calling t() or applyTo().
  window.dmI18nReady = window.dmI18n.init({
    scopes: ['/i18n/{locale}.json']
  });
})();
